// User Store for Metapic V2
// Centralized user profile and stats management with Zustand

import React from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { userService, UserData, UserStatsData } from '../services/userService';
import { useAuthStore } from './authStore';

// Error Types
interface ApiError extends Error {
  status?: number;
  code?: string;
  details?: any;
}

interface NetworkError extends Error {
  code: 'NETWORK_ERROR';
  originalError?: Error;
}

interface TimeoutError extends Error {
  code: 'TIMEOUT_ERROR';
  timeout: number;
}

// Type guards for error handling
const isApiError = (error: unknown): error is ApiError => {
  return error instanceof Error && 'status' in error;
};

const isNetworkError = (error: unknown): error is NetworkError => {
  return error instanceof Error && 'code' in error && (error as any).code === 'NETWORK_ERROR';
};

const isTimeoutError = (error: unknown): error is TimeoutError => {
  return error instanceof Error && 'code' in error && (error as any).code === 'TIMEOUT_ERROR';
};

// Helper function for error handling with development/production granularity
const getErrorMessage = (error: unknown, fallbackMessage: string): string => {
  const isDev = import.meta.env.DEV;
  
  if (isDev) {
    if (isApiError(error)) {
      return `API Error (${error.status}): ${error.message}`;
    }
    if (isNetworkError(error)) {
      return `Network Error: ${error.message}`;
    }
    if (isTimeoutError(error)) {
      return `Timeout Error (${error.timeout}ms): ${error.message}`;
    }
    return error instanceof Error ? error.message : String(error);
  }
  
  return fallbackMessage;
};

// Helper function to create timeout promise (DRY principle)
const createTimeoutPromise = (timeout: number): Promise<never> => {
  return new Promise((_, reject) => {
    setTimeout(() => {
      const timeoutError: TimeoutError = {
        name: 'TimeoutError',
        message: `Request timeout after ${timeout}ms`,
        code: 'TIMEOUT_ERROR',
        timeout
      } as TimeoutError;
      reject(timeoutError);
    }, timeout);
  });
};

// Telemetry helper for monitoring (Sentry/LogRocket integration)
const sendTelemetry = (event: string, data?: Record<string, any>, isError = false) => {
  // Only send telemetry in production or when explicitly enabled
  const shouldSendTelemetry = !import.meta.env.DEV || import.meta.env.VITE_ENABLE_TELEMETRY === 'true';
  
  if (!shouldSendTelemetry) return;
  
  try {
    // Sentry integration (if available)
    if (typeof window !== 'undefined' && (window as any).Sentry) {
      if (isError) {
        (window as any).Sentry.captureException(new Error(event), {
          extra: data,
          tags: { component: 'userStore' }
        });
      } else {
        (window as any).Sentry.addBreadcrumb({
          message: event,
          data,
          category: 'userStore'
        });
      }
    }
    
    // LogRocket integration (if available)
    if (typeof window !== 'undefined' && (window as any).LogRocket) {
      (window as any).LogRocket.track(event, data);
    }
    
    // Custom analytics endpoint (if configured)
    if (import.meta.env.VITE_ANALYTICS_ENDPOINT) {
      fetch(import.meta.env.VITE_ANALYTICS_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event, data, timestamp: Date.now() })
      }).catch(() => {}); // Silently fail analytics
    }
  } catch (error) {
    // Silently fail telemetry to avoid breaking the app
    console.warn('Telemetry failed:', error);
  }
};

interface UserState {
  // Profile data
  profile: UserData | null;
  profileLoading: boolean;
  profileError: string | null;
  profileLastFetched: number | null;
  profileActiveRequest: boolean;
  profileAbortController: AbortController | null;
  
  // Stats data
  stats: UserStatsData | null;
  statsLoading: boolean;
  statsError: string | null;
  statsLastFetched: number | null;
  statsActiveRequest: boolean;
  statsAbortController: AbortController | null;
  
  // Cache configuration
  cacheTimeout: number; // 5 minutes in milliseconds
  requestTimeout: number; // 30 seconds timeout
}

interface UserActions {
  // Profile actions
  fetchProfile: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  clearProfile: () => void;
  
  // Stats actions
  fetchStats: () => Promise<void>;
  refreshStats: () => Promise<void>;
  clearStats: () => void;
  
  // Combined actions
  fetchUserData: () => Promise<void>;
  clearAllUserData: () => void;
  clearUserDataOnLogout: () => void;
  cancelAllRequests: () => void;
  
  // Utility functions
  isProfileCacheValid: () => boolean;
  isStatsCacheValid: () => boolean;
}

type UserStore = UserState & UserActions;

export const useUserStore = create<UserStore>()(persist(
  (set, get) => ({
    // Initial state
    profile: null,
    profileLoading: false,
    profileError: null,
    profileLastFetched: null,
    profileActiveRequest: false,
    profileAbortController: null,
    
    stats: null,
    statsLoading: false,
    statsError: null,
    statsLastFetched: null,
    statsActiveRequest: false,
    statsAbortController: null,
    
    cacheTimeout: 5 * 60 * 1000, // 5 minutes
    requestTimeout: 30 * 1000, // 30 seconds
    
    // Utility functions
    isProfileCacheValid: () => {
      const { profileLastFetched, cacheTimeout } = get();
      if (!profileLastFetched) return false;
      return Date.now() - profileLastFetched < cacheTimeout;
    },
    
    isStatsCacheValid: () => {
      const { statsLastFetched, cacheTimeout } = get();
      if (!statsLastFetched) return false;
      return Date.now() - statsLastFetched < cacheTimeout;
    },
    
    // Profile actions
    fetchProfile: async () => {
      const { isProfileCacheValid, profile, profileActiveRequest, profileAbortController, requestTimeout } = get();
      
      // Cancel previous request if exists
      if (profileAbortController) {
        profileAbortController.abort();
      }
      
      // Prevent concurrent requests
      if (profileActiveRequest) {
        return;
      }
      
      // Return cached data if valid
      if (isProfileCacheValid() && profile) {
        
        // Send cache hit telemetry
        sendTelemetry('user_profile_cache_hit', {
          cacheAge: Date.now() - (get().profileLastFetched || 0)
        });
        return;
      }
      
      // Check if user is authenticated
      if (!useAuthStore.getState().isAuthenticated) {
        set({ 
          profile: null, 
          profileError: 'User not authenticated',
          profileLastFetched: null 
        });
        return;
      }
      
      // Create new AbortController for this request
      const abortController = new AbortController();
      
      set({ 
        profileLoading: true, 
        profileError: null, 
        profileActiveRequest: true,
        profileAbortController: abortController
      });
      
      try {
        const profileData = await Promise.race([
           userService.getCurrentUserData(abortController.signal),
           createTimeoutPromise(requestTimeout)
         ]) as UserData;
        
        // Check if request was aborted
        if (abortController.signal.aborted) {
          return;
        }
        
        set({ 
          profile: profileData,
          profileLoading: false,
          profileError: null,
          profileLastFetched: Date.now(),
          profileActiveRequest: false,
          profileAbortController: null
        });
        
        // Send success telemetry
        sendTelemetry('user_profile_fetch_success', {
          cacheUsed: false,
          fetchTime: Date.now() - (get().profileLastFetched || 0)
        });
      } catch (error: unknown) {
        // Don't update state if request was aborted
        if (abortController.signal.aborted) {
          sendTelemetry('user_profile_fetch_cancelled');
          return;
        }
        
        console.error('Failed to fetch user profile:', error);
        
        // Type-safe error handling and telemetry
        const errorData: Record<string, any> = {
          errorMessage: error instanceof Error ? error.message : 'Unknown error',
          errorType: error instanceof Error ? error.constructor.name : 'Unknown'
        };
        
        if (isApiError(error)) {
          errorData.status = error.status;
          errorData.code = error.code;
        } else if (isNetworkError(error)) {
          errorData.networkError = true;
        } else if (isTimeoutError(error)) {
          errorData.timeout = error.timeout;
        }
        
        sendTelemetry('user_profile_fetch_error', errorData, true);
        
        set({ 
          profileLoading: false,
          profileError: getErrorMessage(error, 'Unable to load profile. Please try again later.'),
          profileActiveRequest: false,
          profileAbortController: null
        });
      }
     },
     
     refreshProfile: async () => {
      // Force refresh by clearing cache timestamp
      set({ profileLastFetched: null });
      await get().fetchProfile();
    },
    
    clearProfile: () => {
      set({ 
        profile: null,
        profileLoading: false,
        profileError: null,
        profileLastFetched: null
      });
    },
    
    // Stats actions
    fetchStats: async () => {
      const { isStatsCacheValid, stats, statsActiveRequest, statsAbortController, requestTimeout } = get();
      
      // Cancel previous request if exists
      if (statsAbortController) {
        statsAbortController.abort();
      }
      
      // Prevent concurrent requests
      if (statsActiveRequest) {
        return;
      }
      
      // Return cached data if valid
      if (isStatsCacheValid() && stats) {
        
        // Send cache hit telemetry
        sendTelemetry('user_stats_cache_hit', {
          cacheAge: Date.now() - (get().statsLastFetched || 0)
        });
        return;
      }
      
      // Check if user is authenticated
      if (!useAuthStore.getState().isAuthenticated) {
        set({ 
          stats: null, 
          statsError: 'User not authenticated',
          statsLastFetched: null 
        });
        return;
      }
      
      // Create new AbortController for this request
      const abortController = new AbortController();
      
      set({ 
        statsLoading: true, 
        statsError: null, 
        statsActiveRequest: true,
        statsAbortController: abortController
      });
      
      try {
        const statsData = await Promise.race([
          userService.getUserStats(abortController.signal),
          createTimeoutPromise(requestTimeout)
        ]) as UserStatsData;
        
        // Check if request was aborted
        if (abortController.signal.aborted) {
          return;
        }
        
        set({ 
          stats: statsData,
          statsLoading: false,
          statsError: null,
          statsLastFetched: Date.now(),
          statsActiveRequest: false,
          statsAbortController: null
        });
        
        // Send success telemetry
        sendTelemetry('user_stats_fetch_success', {
          cacheUsed: false,
          fetchTime: Date.now() - (get().statsLastFetched || 0)
        });
      } catch (error: unknown) {
        // Don't update state if request was aborted
        if (abortController.signal.aborted) {
          sendTelemetry('user_stats_fetch_cancelled');
          return;
        }
        
        console.error('Failed to fetch user stats:', error);
        
        // Type-safe error handling and telemetry
        const errorData: Record<string, any> = {
          errorMessage: error instanceof Error ? error.message : 'Unknown error',
          errorType: error instanceof Error ? error.constructor.name : 'Unknown'
        };
        
        if (isApiError(error)) {
          errorData.status = error.status;
          errorData.code = error.code;
        } else if (isNetworkError(error)) {
          errorData.networkError = true;
        } else if (isTimeoutError(error)) {
          errorData.timeout = error.timeout;
        }
        
        sendTelemetry('user_stats_fetch_error', errorData, true);
        
        set({ 
          statsLoading: false,
          statsError: getErrorMessage(error, 'Unable to load statistics. Please try again later.'),
          statsActiveRequest: false,
          statsAbortController: null
        });
      }
     },
     
     refreshStats: async () => {
      // Force refresh by clearing cache timestamp
      set({ statsLastFetched: null });
      await get().fetchStats();
    },
    
    clearStats: () => {
      set({ 
        stats: null,
        statsLoading: false,
        statsError: null,
        statsLastFetched: null
      });
    },
    
    // Combined actions
    fetchUserData: async () => {
      // Fetch both profile and stats concurrently with consistent error handling
      const results = await Promise.allSettled([
        get().fetchProfile(),
        get().fetchStats()
      ]);
      
      // Log any failures for debugging (in development mode)
      if (import.meta.env.DEV) {
        results.forEach((result, index) => {
          if (result.status === 'rejected') {
            const operation = index === 0 ? 'profile' : 'stats';
            console.warn(`Failed to fetch ${operation}:`, result.reason);
          }
        });
      }
    },
    
    clearAllUserData: () => {
      get().clearProfile();
      get().clearStats();
    },
    
    clearUserDataOnLogout: () => {
      // Cancel any ongoing requests first
      get().cancelAllRequests();
      
      set({ 
        profile: null, 
        stats: null, 
        profileError: null, 
        statsError: null,
        profileLastFetched: null,
        statsLastFetched: null,
        profileActiveRequest: false,
        statsActiveRequest: false,
        profileAbortController: null,
        statsAbortController: null
      });
    },
    
    cancelAllRequests: () => {
      const { profileAbortController, statsAbortController } = get();
      
      if (profileAbortController) {
        profileAbortController.abort();
      }
      
      if (statsAbortController) {
        statsAbortController.abort();
      }
      
      set({
        profileAbortController: null,
        statsAbortController: null,
        profileActiveRequest: false,
        statsActiveRequest: false
      });
    }
  }),
  {
    name: 'user-store',
    // Only persist non-sensitive data and cache timestamps
    partialize: (state) => ({
      profileLastFetched: state.profileLastFetched,
      statsLastFetched: state.statsLastFetched,
      cacheTimeout: state.cacheTimeout
    })
  }
));

// Export hook for easy access to user profile data
export const useUserProfile = () => {
  const { profile, profileLoading, profileError, fetchProfile } = useUserStore();
  
  // Auto-fetch profile if not loaded (with SSR safety)
  React.useEffect(() => {
    // SSR safety guard - only run in browser environment
    if (typeof window === 'undefined') return;
    
    if (!profile && !profileLoading && !profileError) {
      fetchProfile();
    }
  }, [profile, profileLoading, profileError, fetchProfile]);
  
  return { profile, profileLoading, profileError, refetch: fetchProfile };
};

// Export hook for easy access to user stats data
export const useUserStats = () => {
  const { stats, statsLoading, statsError, fetchStats } = useUserStore();
  
  // Auto-fetch stats if not loaded (with SSR safety)
  React.useEffect(() => {
    // SSR safety guard - only run in browser environment
    if (typeof window === 'undefined') return;
    
    if (!stats && !statsLoading && !statsError) {
      fetchStats();
    }
  }, [stats, statsLoading, statsError, fetchStats]);
  
  return { stats, statsLoading, statsError, refetch: fetchStats };
};

export default useUserStore;